import { useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';
import { useSessionStore } from '@/store/session.store';
import { useAccountStore } from '@/store/account.store';
import { useReadersStore } from '@/store/readers.store';
import { setUnauthorizedHandler, resetUnauthorizedHandler } from '@/services/http';
import { env } from '@/lib/env';

/** Auth0 SDK error codes that mean the session / refresh token is dead. */
const AUTH0_SESSION_EXPIRED_CODES = new Set([
  'login_required',
  'missing_refresh_token',
  'invalid_grant',
  'consent_required',
]);

function isSessionExpiredError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const code = (err as { error?: unknown }).error;
  return typeof code === 'string' && AUTH0_SESSION_EXPIRED_CODES.has(code);
}

/**
 * Keeps `SessionStore.accessToken` in sync with the Auth0 SDK and
 * installs the global 401 handler for `apiRequest()`.
 *
 * We cache the token in Zustand so service modules can call
 * `apiRequest()` from any call site (event handlers, store actions,
 * useEffect) without having to thread `getAccessTokenSilently` through
 * every layer. Auth0 handles the actual token storage + refresh.
 *
 * Two failure paths both route to the same session-expired flow:
 *   1. The API rejects a request with 401 (token was stale / revoked).
 *   2. `getAccessTokenSilently()` itself fails with a session-expired
 *      error code — typically after long idle when the refresh token
 *      has also expired. Without this, `ProtectedRoute` would stay on
 *      the PageLoader forever because `isAuthenticated` is still true
 *      but `accessToken` never lands.
 *
 * Transient network errors from `getAccessTokenSilently` fall through
 * (warn + leave accessToken null) so a reconnect can recover.
 */
export function ApiAuthBridge() {
  const { isAuthenticated, logout, getAccessTokenSilently } = useAuth0();
  const setAccessToken = useSessionStore((s) => s.setAccessToken);

  const handleSessionExpired = useCallback(() => {
    toast.error('Your session has expired. Please sign in again.');
    useReadersStore.getState().reset();
    useAccountStore.getState().reset();
    useSessionStore.getState().reset();
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      if (!isAuthenticated) {
        setAccessToken(null);
        return;
      }
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: env.auth0Audience },
        });
        if (!cancelled) setAccessToken(token);
      } catch (err) {
        console.warn('[auth] failed to fetch access token', err);
        if (cancelled) return;
        setAccessToken(null);
        if (isSessionExpiredError(err)) {
          handleSessionExpired();
        }
      }
    }
    void refresh();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getAccessTokenSilently, setAccessToken, handleSessionExpired]);

  useEffect(() => {
    // Only authenticated sessions get the 401 → logout handler. A guest has
    // no session to expire, and a stray 401 from an accidental auth-only
    // call must NOT kick off an Auth0 logout redirect (guest features gate
    // themselves at the component level instead).
    if (!isAuthenticated) {
      setUnauthorizedHandler(null);
      return;
    }
    resetUnauthorizedHandler();
    setUnauthorizedHandler(handleSessionExpired);
    return () => {
      setUnauthorizedHandler(null);
    };
  }, [isAuthenticated, handleSessionExpired]);

  return null;
}
